/**
 * Spot Detail Modal - 스팟 상세 정보 모달
 */

import { motion } from 'framer-motion';
import { HiX, HiLocationMarker, HiSparkles } from 'react-icons/hi';

export default function SpotDetailModal({ spot, onClose }) {
  if (!spot) return null;

  // 자기장 강도에 따른 색상 및 텍스트
  const getStrengthInfo = (strength) => {
    switch (strength) {
      case 'strong':
        return { color: '#ef4444', text: '강함', bg: 'bg-red-100' };
      case 'medium':
        return { color: '#f59e0b', text: '보통', bg: 'bg-yellow-100' };
      case 'weak':
        return { color: '#3b82f6', text: '약함', bg: 'bg-blue-100' };
      default:
        return { color: '#6366f1', text: '알 수 없음', bg: 'bg-gray-100' };
    }
  };

  const strengthInfo = getStrengthInfo(spot.magnetic_field_strength);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="w-full max-w-md rounded-t-3xl shadow-2xl overflow-hidden"
        style={{ background: 'var(--color-surface)' }}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          className="px-6 py-4 relative"
          style={{
            background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
          }}
        >
          <motion.button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-all"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <HiX className="w-5 h-5" />
          </motion.button>
          
          <div className="pr-12">
            <h2 className="text-2xl font-display font-bold text-white mb-2">
              {spot.spot_name}
            </h2>
            <div className="flex items-center gap-2">
              <span
                className="px-3 py-1 rounded-full text-xs font-semibold text-white shadow-lg"
                style={{ backgroundColor: strengthInfo.color }}
              >
                자기장 {strengthInfo.text}
              </span>
              <span className="text-sm text-white/90">
                {spot.nanotesla_value?.toLocaleString() || '0'} nT
              </span>
            </div>
          </div>
        </div>

        {/* 내용 */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* 주소 */}
          <div className="flex items-start gap-3">
            <HiLocationMarker className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-primary)' }} />
            <div className="flex-1">
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
                주소
              </p>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {spot.address || `${spot.latitude.toFixed(6)}, ${spot.longitude.toFixed(6)}`}
              </p>
            </div>
          </div>

          {/* 간단 설명 */}
          {spot.short_description && (
            <div>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text)' }}>
                간단 설명
              </p>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                {spot.short_description}
              </p>
            </div>
          )}

          {/* 상세 효능 */}
          {spot.detailed_benefits && (
            <div 
              className="p-4 rounded-xl relative overflow-visible" 
              style={{ 
                background: 'var(--color-background)',
                border: '3px solid var(--color-accent)',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)'
              }}
            >
              {/* OpenAI 공식 로고 - 테두리 위에 걸쳐 표시 */}
              <div 
                className="absolute -top-4 -right-4 w-16 h-16 rounded-full flex items-center justify-center z-20 shadow-lg" 
                style={{ 
                  background: `linear-gradient(135deg, #10A37F, #0D8C6F)`,
                  border: '4px solid var(--color-background)',
                  boxShadow: '0 4px 12px rgba(16, 163, 127, 0.4)'
                }}
              >
                <svg 
                  width="36" 
                  height="36" 
                  viewBox="0 0 24 24" 
                  fill="#FFFFFF" 
                  fillRule="evenodd" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
                >
                  <title>OpenAI</title>
                  <path d="M21.55 10.004a5.416 5.416 0 00-.478-4.501c-1.217-2.09-3.662-3.166-6.05-2.66A5.59 5.59 0 0010.831 1C8.39.995 6.224 2.546 5.473 4.838A5.553 5.553 0 001.76 7.496a5.487 5.487 0 00.691 6.5 5.416 5.416 0 00.477 4.502c1.217 2.09 3.662 3.165 6.05 2.66A5.586 5.586 0 0013.168 23c2.443.006 4.61-1.546 5.361-3.84a5.553 5.553 0 003.715-2.66 5.488 5.488 0 00-.693-6.497v.001zm-8.381 11.558a4.199 4.199 0 01-2.675-.954c.034-.018.093-.05.132-.074l4.44-2.53a.71.71 0 00.364-.623v-6.176l1.877 1.069c.02.01.033.029.036.05v5.115c-.003 2.274-1.87 4.118-4.174 4.123zM4.192 17.78a4.059 4.059 0 01-.498-2.763c.032.02.09.055.131.078l4.44 2.53c.225.13.504.13.73 0l5.42-3.088v2.138a.068.068 0 01-.027.057L9.9 19.288c-1.999 1.136-4.552.46-5.707-1.51h-.001zM3.023 8.216A4.15 4.15 0 015.198 6.41l-.002.151v5.06a.711.711 0 00.364.624l5.42 3.087-1.876 1.07a.067.067 0 01-.063.005l-4.489-2.559c-1.995-1.14-2.679-3.658-1.53-5.63h.001zm15.417 3.54l-5.42-3.088L14.896 7.6a.067.067 0 01.063-.006l4.489 2.557c1.998 1.14 2.683 3.662 1.529 5.633a4.163 4.163 0 01-2.174 1.807V12.38a.71.71 0 00-.363-.623zm1.867-2.773a6.04 6.04 0 00-.132-.078l-4.44-2.53a.731.731 0 00-.729 0l-5.42 3.088V7.325a.068.068 0 01.027-.057L14.1 4.713c2-1.137 4.555-.46 5.707 1.513.487.833.664 1.809.499 2.757h.001zm-11.741 3.81l-1.877-1.068a.065.065 0 01-.036-.051V6.559c.001-2.277 1.873-4.122 4.181-4.12.976 0 1.92.338 2.671.954-.034.018-.092.05-.131.073l-4.44 2.53a.71.71 0 00-.365.623l-.003 6.173v.002zm1.02-2.168L12 9.25l2.414 1.375v2.75L12 14.75l-2.415-1.375v-2.75z"></path>
                </svg>
              </div>
              
              {/* AI 배지 - 카드 테두리 위에 걸쳐 표시 */}
              <div 
                className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center z-20 shadow-lg" 
                style={{ 
                  background: 'linear-gradient(135deg, #FF6B9D, #C44569)',
                  border: '3px solid var(--color-background)',
                  boxShadow: '0 2px 8px rgba(255, 107, 157, 0.4)'
                }}
              >
                <span className="text-[10px] font-bold text-white leading-none">AI</span>
              </div>
              
              {/* 배경 그라데이션 효과 */}
              <div 
                className="absolute inset-0 rounded-xl pointer-events-none opacity-5"
                style={{
                  background: `linear-gradient(135deg, var(--color-accent), var(--color-primary))`,
                }}
              />
              
              <div className="flex items-center gap-2 mb-2 relative z-10">
                <HiSparkles className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
                  상세 효능
                </p>
              </div>
              <p className="text-sm leading-relaxed relative z-10 pr-8" style={{ color: 'var(--color-text-secondary)' }}>
                {spot.detailed_benefits}
              </p>
            </div>
          )}

          {/* 추가 정보 */}
          <div className="pt-4 border-t-2" style={{ borderColor: 'var(--color-primary)' }}>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  자기장 강도
                </p>
                <p className="font-bold" style={{ color: strengthInfo.color }}>
                  {strengthInfo.text}
                </p>
              </div>
              <div>
                <p className="font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  측정값
                </p>
                <p className="font-bold" style={{ color: 'var(--color-text)' }}>
                  {spot.nanotesla_value?.toLocaleString() || '0'} nT
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
